import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * ValidationErrorModal - Displays validation errors in a modal dialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Called when modal is closed
 * @param {Array<string|Object>} props.errors - Array of error messages or error objects
 * @param {string} [props.title='Validation Errors'] - Modal title
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {string} [props.closeText='Close'] - Close button text
 * @param {string} [props.severity='error'] - 'error' or 'warning'
 *
 * @example
 * // Simple string errors
 * <ValidationErrorModal
 *   open={showErrors}
 *   onClose={() => setShowErrors(false)}
 *   errors={['CUSIP is required', 'Price must be a number']}
 * />
 *
 * // Object errors with field names
 * <ValidationErrorModal
 *   open={showErrors}
 *   onClose={() => setShowErrors(false)}
 *   errors={[
 *     { field: 'CUSIP', message: 'Required field' },
 *     { field: 'Price', message: 'Must be a number' }
 *   ]}
 *   title="Please fix the following errors"
 * />
 */
export const ValidationErrorModal = ({
  open,
  onClose,
  errors = [],
  title = 'Validation Errors',
  subtitle,
  closeText = 'Close',
  severity = 'error',
}) => {
  const Icon = severity === 'warning' ? WarningIcon : ErrorIcon;
  const iconColor = severity === 'warning' ? 'warning.main' : 'error.main';

  const formatError = (error, index) => {
    if (typeof error === 'string') {
      return error;
    }
    if (error.field && error.message) {
      return `${error.field}: ${error.message}`;
    }
    if (error.message) {
      return error.message;
    }
    return JSON.stringify(error);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon sx={{ color: iconColor }} />
        {title}
      </DialogTitle>
      <DialogContent>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {errors.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No errors to display.
          </Typography>
        ) : (
          <List dense>
            {errors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: iconColor,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={formatError(error, index)}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" autoFocus>
          {closeText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationErrorModal;
